import { draftReviewGaps } from "./cpDraftFields";

export const OVERLAP_MESSAGE = "Fields overlap. Move them apart before publishing.";
export const testStateEnabled = (environment, nodeEnvironment) => ["dev", "development", "local"].includes(environment)
  || (environment === undefined && nodeEnvironment === "development");
// ENG-2482: the builder offers one document type for now.
export const CP_AGREEMENT = "cp_agreement";
export const typeLabel = (key = "") => key === CP_AGREEMENT ? "CP agreement" : key.split(/[_-]+/).filter(Boolean).map((word) => word[0].toUpperCase() + word.slice(1)).join(" ");
export const typeKey = (name) => name.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/^[^a-z]+/, "").slice(0, 64).replace(/-+$/, "");

// Touching edges are allowed. The same page is shared by every signer.
export function overlappingFieldKeys(fields) {
  const keys = new Set();
  fields.forEach((field, index) => fields.slice(index + 1).forEach((other) => {
    if (field.page_number === other.page_number
      && Math.min(field.position_x + field.width, other.position_x + other.width) - Math.max(field.position_x, other.position_x) > 1e-10
      && Math.min(field.position_y + field.height, other.position_y + other.height) - Math.max(field.position_y, other.position_y) > 1e-10) {
      keys.add(field.field_key);
      keys.add(other.field_key);
    }
  }));
  return keys;
}

export function builderChecklist(draft, sources, items) {
  // Associate each problem with the field that introduces it into the actual
  // validator's output. Labels repeat across signers and are not an identity.
  const fieldByGap = new Map();
  const partial = { ...draft, fields: [] };
  let previousGaps = new Set(draftReviewGaps(partial, sources, items));
  for (const field of draft.fields) {
    partial.fields.push(field);
    const nextGaps = new Set(draftReviewGaps(partial, sources, items));
    for (const message of nextGaps) {
      if (!previousGaps.has(message) && !fieldByGap.has(message)) fieldByGap.set(message, field);
    }
    previousGaps = nextGaps;
  }
  const rows = draftReviewGaps(draft, sources, items).map((message) => {
    const field = fieldByGap.get(message);
    const fieldsStep = Boolean(field) || /signature field|every field|field list/.test(message);
    const role = /for the physician/.test(message) ? "physician" : /for the clinician/.test(message) ? "operator" : null;
    return { message, step: fieldsStep ? 1 : 0, fieldKey: field?.field_key || null, role };
  });
  const overlaps = overlappingFieldKeys(draft.fields);
  if (overlaps.size) rows.push({ message: OVERLAP_MESSAGE, step: 1, fieldKey: [...overlaps][0] });
  return rows;
}

export function templateRows(catalog) {
  const versions = catalog.published_versions || [];
  const mappings = catalog.mappings || [];
  const linked = new Set();
  const rows = (catalog.drafts || []).map((draft) => {
    const published = versions.filter((version) => version.draft_id === draft.id).sort((a, b) => b.version_number - a.version_number);
    const latest = published[0];
    published.forEach((version) => linked.add(version.id));
    const currentMappings = mappings.filter((mapping) => published.some((version) => version.id === mapping.template_version_id));
    const live = currentMappings.some((mapping) => mapping.is_active === 1);
    const current = published.find((version) => currentMappings.some((mapping) => mapping.template_version_id === version.id && mapping.is_active === 1)) || latest;
    const changed = Boolean(latest && Number.isInteger(latest.source_revision) && draft.revision > latest.source_revision);
    return { ...draft, editId: draft.id, rowKey: `draft-${draft.id}`, version: current, mappings: currentMappings, status: live ? "Live" : currentMappings.length ? "Retired" : "Draft", changed, published };
  });
  versions.filter((version) => !linked.has(version.id)).forEach((version) => {
    const currentMappings = mappings.filter((mapping) => mapping.template_version_id === version.id);
    rows.push({ ...version, editId: version.draft_id || null, rowKey: `version-${version.id}`, version, mappings: currentMappings, status: currentMappings.some((mapping) => mapping.is_active === 1) ? "Live" : "Retired", published: [version] });
  });
  return rows;
}
